import React from "react";
import { Box } from "@amzn/awsui-components-console";
import { ValidationError } from "@amzn/fuse-accounting-central-datastore-client";

interface FocusedField {
  fieldName: string;
  useCaseName?: string | null;
  uslLineIndex?: number | null;
}

export const useValidationUtils = (
  validationErrors: ValidationError[],
  focusedField?: FocusedField | null,
) => {
  const getFieldErrors = (
    fieldName: string,
    useCaseName?: string | null,
    uslLineIndex?: number | null,
  ) => {
    return validationErrors.filter((error) => {
      // Global field: useCaseName is null/undefined
      if (useCaseName == null) {
        return (
          error.derivedAttributes?.includes(fieldName) ||
          error.derivingAttributes?.includes(fieldName)
        );
      }

      // Use case specific field: useCaseName and uslLineIndex are provided
      if (useCaseName != null && uslLineIndex != null) {
        const matchesUseCase = error.useCase === useCaseName;
        const matchesLine = error.uslLine === uslLineIndex;
        const matchesField =
          error.derivedAttributes?.includes(fieldName) ||
          error.derivingAttributes?.includes(fieldName);

        return matchesUseCase && matchesLine && matchesField;
      }

      return false;
    });
  };

  const getFieldValidationProps = (
    fieldName: string,
    useCaseName?: string | null,
    uslLineIndex?: number | null,
  ) => {
    const errors = getFieldErrors(fieldName, useCaseName, uslLineIndex);
    if (errors.length === 0) return {};

    const hasErrorField = errors.some((error) =>
      error.derivedAttributes?.includes(fieldName),
    );

    // Only apply invalid prop for actual errors, not for warnings (derivingAttributes)
    if (hasErrorField) {
      return { invalid: true };
    }

    // Don't apply warning prop to avoid styling issues with Select components
    return {};
  };

  const getFieldTextColor = (
    fieldName: string,
    useCaseName?: string | null,
    uslLineIndex?: number | null,
  ) => {
    const errors = getFieldErrors(fieldName, useCaseName, uslLineIndex);
    if (errors.length === 0) return "inherit";

    const hasErrorField = errors.some((error) =>
      error.derivedAttributes?.includes(fieldName),
    );
    const hasInfoField = errors.some((error) =>
      error.derivingAttributes?.includes(fieldName),
    );

    if (hasErrorField) {
      return "#d13212"; // Red for derived attributes
    } else if (hasInfoField) {
      // Focus-based highlighting logic for derivingAttributes
      if (focusedField) {
        // Check if the currently focused field is a derivedAttribute that this field contributes to
        const relevantErrors = validationErrors.filter((error) => {
          const focusMatches =
            focusedField.useCaseName == null
              ? error.derivedAttributes?.includes(focusedField.fieldName)
              : error.useCase === focusedField.useCaseName &&
                error.uslLine === focusedField.uslLineIndex &&
                error.derivedAttributes?.includes(focusedField.fieldName);

          const thisFieldMatches =
            useCaseName == null
              ? error.derivingAttributes?.includes(fieldName)
              : error.useCase === useCaseName &&
                error.uslLine === uslLineIndex &&
                error.derivingAttributes?.includes(fieldName);

          return focusMatches && thisFieldMatches;
        });

        // Only highlight if this field is relevant to the focused derived attribute
        if (relevantErrors.length > 0) {
          return "#855900"; // Yellow for relevant deriving attributes
        } else {
          return "inherit"; // Normal color for non-relevant deriving attributes
        }
      } else {
        // No field focused - show all deriving attributes in yellow (default behavior)
        return "#855900"; // Yellow for deriving attributes
      }
    }

    return "inherit";
  };

  const getFieldTooltipContent = (
    fieldName: string,
    useCaseName?: string | null,
    uslLineIndex?: number | null,
  ) => {
    const errors = getFieldErrors(fieldName, useCaseName, uslLineIndex);
    if (errors.length === 0) return null;

    const uniqueMessages: String[] = [];
    errors.forEach((error) => {
      if (error.message && !uniqueMessages.includes(error.message)) {
        uniqueMessages.push(error.message);
      }
    });
    return (
      <Box>
        {uniqueMessages.map((message, index) => (
          <div
            key={index}
            style={{
              marginBottom: index < uniqueMessages.length - 1 ? "8px" : "0",
            }}
          >
            {message}
          </div>
        ))}
      </Box>
    );
  };

  return {
    getFieldErrors,
    getFieldValidationProps,
    getFieldTextColor,
    getFieldTooltipContent,
  };
};
